import React from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { LinkifiedText } from "./LinkifiedText";

function hasDescription(topic) {
  return Boolean(topic?.description_student || topic?.description_teacher);
}

/**
 * TopicInfoTrigger — ícono (i) que, al hacer clic/tap, abre un popover con la
 * descripción de uno o más temas — donde el tema aparece en el contexto de
 * una clase (card, evento de calendario), no en un catálogo aparte.
 *
 * `trigger="click"` (no hover): funciona igual en desktop y en touch/móvil.
 * `stopPropagation` en el botón: estas cards/eventos suelen ser clicables por
 * sí mismos (abren el detalle de la clase, o el calendario arrastra/reprograma)
 * — el ícono no debe disparar esa acción del contenedor.
 *
 * No renderiza nada si ningún tema trae descripción — nunca un ícono "muerto".
 */
function TopicInfoTrigger({ topics, className }) {
  const list = Array.isArray(topics) ? topics : [topics];
  const withInfo = list.filter(hasDescription);
  if (withInfo.length === 0) return null;

  const popover = (
    <Popover>
      <Popover.Body style={{ maxWidth: 280 }}>
        {withInfo.map((t, i) => (
          <div
            key={t.id ?? t.name ?? i}
            className={i > 0 ? "mt-2 pt-2 border-top" : undefined}
          >
            {withInfo.length > 1 && <div className="fw-medium small mb-1">{t.name}</div>}
            {t.description_student && (
              <div className={t.description_teacher ? "mb-1" : undefined}>
                {t.description_teacher && (
                  <div className="text-muted" style={{ fontSize: ".7rem" }}>
                    Para estudiantes
                  </div>
                )}
                <LinkifiedText text={t.description_student} className="small" />
              </div>
            )}
            {t.description_teacher && (
              <div>
                <div className="text-muted" style={{ fontSize: ".7rem" }}>
                  Para docentes
                </div>
                <LinkifiedText text={t.description_teacher} className="small" />
              </div>
            )}
          </div>
        ))}
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger trigger="click" placement="auto" overlay={popover} rootClose>
      <button
        type="button"
        className={`btn btn-link p-0 border-0 text-reset lh-1 ${className ?? ""}`}
        aria-label="Ver descripción del tema"
        onClick={(e) => e.stopPropagation()}
      >
        <i className="mdi mdi-information-outline" aria-hidden="true" />
      </button>
    </OverlayTrigger>
  );
}

export { TopicInfoTrigger };
